import { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RecordCard } from '../mylife/RecordCard';

/**
 * Cabinet tab: drawers (categories → types) down the side, records in the
 * middle, and a search box that looks *inside* what they wrote (server
 * decrypts per request; results carry a snippet).
 */
export const OrgCabinet = ({ records, onVisible, pendingCount, cabinet, drawer, onDrawer, type, onType, search, onSearch, searchResults, searching, onSelect }) => {
  const [year, setYear] = useState('all');
  const [sort, setSort] = useState('newest');
  const [only, setOnly] = useState({ redactions: false, copies: false, low: false });
  const drawers = cabinet?.drawers || [];
  const typeCategory = useMemo(() => cabinet?.type_categories || {}, [cabinet]);
  const years = cabinet?.totals?.years || [];

  const shown = useMemo(() => {
    let list = searchResults || records;
    if (!searchResults) {
      if (drawer) list = list.filter((r) => (typeCategory[r.doc_type] || 'other') === drawer);
      if (type) list = list.filter((r) => r.doc_type === type);
    }
    if (year !== 'all') list = list.filter((r) => String(r.year || (r.date || '').slice(0, 4)) === year);
    if (only.redactions) list = list.filter((r) => r.has_redactions);
    if (only.copies) list = list.filter((r) => (r.source_count || 0) > 1);
    if (only.low) list = list.filter((r) => r.low_confidence);
    const key = (r) => r.date || (r.year ? `${r.year}` : '') || '';
    list = [...list].sort((a, b) => (sort === 'newest' ? key(b).localeCompare(key(a)) : key(a).localeCompare(key(b))));
    return list;
  }, [records, searchResults, drawer, type, typeCategory, year, only, sort]);

  const Pill = ({ on, onClick, children, testId }) => (
    <button type="button" onClick={onClick} aria-pressed={on} data-testid={testId}
      className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors duration-state ${on ? 'bg-brand-wash text-brand-ink border-brand-wash' : 'bg-ground text-ink-70 border-rule hover:bg-inset'}`}>
      {children}
    </button>
  );

  const currentLabel = searchResults
    ? `${shown.length} of ${records.length} mention “${search.trim()}”`
    : type ? `${drawers.find((d) => d.category === drawer)?.label || ''} › ${type} · ${shown.length}`
      : drawer ? `${drawers.find((d) => d.category === drawer)?.label || drawer} · ${shown.length}`
        : `Everything · ${shown.length}`;

  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-6 items-start" data-testid="org-cabinet">
      <aside className="rounded-lg bg-ground p-2 flex flex-col gap-0.5 lg:sticky lg:top-4" aria-label="Drawers">
        <button type="button" onClick={() => { onDrawer(null); onType(null); }}
          className={`flex justify-between items-center px-3 py-2.5 rounded-md text-[15px] font-semibold ${!drawer ? 'bg-inset text-ink' : 'text-ink-70 hover:bg-inset/60'}`}>
          <span>Everything</span><span className="pd-mono">{records.length}</span>
        </button>
        {drawers.map((d) => (
          <div key={d.category}>
            <button type="button" onClick={() => { onDrawer(d.category); onType(null); }}
              className={`w-full flex justify-between items-center px-3 py-2.5 rounded-md text-[15px] font-semibold ${drawer === d.category && !type ? 'bg-inset text-ink' : 'text-ink-70 hover:bg-inset/60'}`}
              data-testid={`cabinet-drawer-${d.category}`}>
              <span>{d.label}</span><span className="pd-mono">{d.count}</span>
            </button>
            {drawer === d.category && d.types.map((t) => (
              <button key={t.name} type="button" onClick={() => onType(type === t.name ? null : t.name)}
                className={`w-full flex justify-between items-center pl-7 pr-3 py-2 rounded-md text-sm ${type === t.name ? 'text-ink font-semibold bg-inset' : 'text-ink-70 hover:bg-inset/60'}`}>
                <span className="truncate">{t.name}</span><span className="pd-mono">{t.count}</span>
              </button>
            ))}
          </div>
        ))}
      </aside>

      <div className="space-y-4 min-w-0">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search inside what they wrote about you" aria-label="Search inside records" className="pr-10" data-testid="cabinet-search" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-55" aria-hidden="true">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </span>
          </div>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="sm:w-[150px]" aria-label="Year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any year</SelectItem>
              {years.map((y) => <SelectItem key={y.year} value={String(y.year)}>{y.year} · {y.count}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="sm:w-[160px]" aria-label="Sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="pd-eyebrow mr-1" data-testid="cabinet-current">{currentLabel}</span>
          <Pill on={only.redactions} onClick={() => setOnly({ ...only, redactions: !only.redactions })} testId="cabinet-only-redactions">Redactions only</Pill>
          <Pill on={only.copies} onClick={() => setOnly({ ...only, copies: !only.copies })}>More than one copy</Pill>
          <Pill on={only.low} onClick={() => setOnly({ ...only, low: !only.low })}>Low confidence</Pill>
          {pendingCount > 0 && !searchResults && (
            <span className="text-sm text-ink-55 flex items-center gap-1.5 ml-auto"><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Unlocking {records.length - pendingCount} of {records.length}</span>
          )}
        </div>
        {shown.length === 0 ? (
          <div className="rounded-lg bg-ground py-10 px-6 text-center">
            <p className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink mb-1">{searchResults ? 'No record mentions that' : 'Nothing in this drawer yet'}</p>
            <p className="text-base text-ink-55">{searchResults ? 'Try another word, or a name.' : 'Records appear here once documents from this organisation have been read.'}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {shown.map((r) => (
              <div key={r.id} className="flex flex-col gap-1">
                <RecordCard record={r} onClick={() => onSelect(r.id)} onVisible={onVisible} />
                {r.snippet && <p className="text-sm text-ink-70 px-5 sm:px-6 -mt-0.5" data-testid={`cabinet-snippet-${r.id}`}>{r.snippet}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgCabinet;
